/**
 * Admin Agent Chat Slice
 *
 * Redux slice for managing admin agent chat state, messages, and streaming.
 * Uses the shared ai-chat-core patterns adapted for the admin agent endpoint.
 */

import {
  type ActivityType,
  type AssistantStatus,
  type ChatMessage,
  type SSEPerformanceMetrics,
} from '@features/ai-chat-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

export interface AgentToolCall {
  toolCallId: string;
  toolCallName: string;
  args: string;
}

export interface MarkdownPayload {
  id: string;
  title: string;
  markdown: string;
  timestamp: number;
}

export interface AgentChatState {
  // Session (from CUSTOM agent_result event)
  sessionId: string | null;
  threadId: string | null;

  // Skill
  currentSkill: string;

  // Messages
  messages: ChatMessage[];

  // Activity status messages
  activities: Record<
    string,
    {
      messageId: string;
      activityType: ActivityType;
      message: string;
      timestamp: number;
    }
  >;

  // Streaming state
  assistant: {
    status: AssistantStatus;
    currentStep: string | null;
    streamingContent: string;
    currentMessageId: string | null;
    firstTokenReceived: boolean;
  };

  // Active tool calls (for UI display)
  activeToolCalls: AgentToolCall[];

  // Markdown payloads (from CUSTOM markdown_payload events)
  markdownPayloads: MarkdownPayload[];
  activePayloadIndex: number | null;

  // UI state
  isChatVisible: boolean;

  // Error handling
  error: string | null;

  // SSE performance metrics
  ssePerformanceMetrics: SSEPerformanceMetrics | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AgentChatState = {
  sessionId: null,
  threadId: null,
  currentSkill: 'general-admin',
  messages: [],
  activities: {},
  assistant: {
    status: 'idle',
    currentStep: null,
    streamingContent: '',
    currentMessageId: null,
    firstTokenReceived: false,
  },
  activeToolCalls: [],
  markdownPayloads: [],
  activePayloadIndex: null,
  isChatVisible: true,
  error: null,
  ssePerformanceMetrics: null,
};

// ============================================================================
// Slice
// ============================================================================

export const agentChatSlice = createSlice({
  name: 'adminAgent/chat',
  initialState,
  reducers: {
    // ========================================================================
    // Session Management
    // ========================================================================

    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },

    setThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },

    setCurrentSkill: (state, action: PayloadAction<string>) => {
      state.currentSkill = action.payload;
    },

    // ========================================================================
    // Message Management
    // ========================================================================

    addUserMessage: (state, action: PayloadAction<string>) => {
      const now = new Date();
      state.messages.push({
        id: `user-${now.getTime()}`,
        role: 'user',
        content: action.payload,
        timestamp: now.toISOString(),
      });
    },

    // ========================================================================
    // Streaming State
    // ========================================================================

    setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
      state.assistant.status = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.assistant.currentStep = action.payload;
    },

    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.assistant.currentMessageId = action.payload;
      state.assistant.streamingContent = '';
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'streaming';
    },

    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.assistant.streamingContent += action.payload;
      if (!state.assistant.firstTokenReceived) {
        state.assistant.firstTokenReceived = true;
      }
    },

    finalizeMessage: state => {
      if (state.assistant.streamingContent) {
        const now = new Date();
        state.messages.push({
          id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
          role: 'assistant',
          content: state.assistant.streamingContent,
          timestamp: now.toISOString(),
        });
      }
      state.assistant.streamingContent = '';
      state.assistant.currentMessageId = null;
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'thinking';
      // Clear activities when message is finalized
      state.activities = {};
    },

    // ========================================================================
    // Activity Management
    // ========================================================================

    setActivitySnapshot: (
      state,
      action: PayloadAction<{
        messageId: string;
        activityType: ActivityType;
        message: string;
        timestamp: number;
        replace?: boolean;
      }>,
    ) => {
      const { messageId, activityType, message, timestamp, replace = true } = action.payload;

      if (!replace && state.activities[messageId]) {
        return;
      }

      state.activities[messageId] = {
        messageId,
        activityType,
        message,
        timestamp,
      };
    },

    clearActivity: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      const { [messageId]: _removed, ...remaining } = state.activities;
      state.activities = remaining;
    },

    clearAllActivities: state => {
      state.activities = {};
    },

    // ========================================================================
    // Tool Call Tracking
    // ========================================================================

    addToolCall: (
      state,
      action: PayloadAction<{ toolCallId: string; toolCallName: string }>,
    ) => {
      state.activeToolCalls.push({
        toolCallId: action.payload.toolCallId,
        toolCallName: action.payload.toolCallName,
        args: '',
      });
    },

    appendToolCallArgs: (
      state,
      action: PayloadAction<{ toolCallId: string; delta: string }>,
    ) => {
      const toolCall = state.activeToolCalls.find(
        tc => tc.toolCallId === action.payload.toolCallId,
      );
      if (toolCall) {
        toolCall.args += action.payload.delta;
      }
    },

    removeToolCall: (state, action: PayloadAction<string>) => {
      state.activeToolCalls = state.activeToolCalls.filter(
        tc => tc.toolCallId !== action.payload,
      );
    },

    clearToolCalls: state => {
      state.activeToolCalls = [];
    },

    // ========================================================================
    // Markdown Payloads
    // ========================================================================

    addMarkdownPayload: (
      state,
      action: PayloadAction<{ title: string; markdown: string }>,
    ) => {
      const payload: MarkdownPayload = {
        id: `mdp-${Date.now()}`,
        title: action.payload.title,
        markdown: action.payload.markdown,
        timestamp: Date.now(),
      };
      state.markdownPayloads.push(payload);
      // Auto-display new payload
      state.activePayloadIndex = state.markdownPayloads.length - 1;
    },

    setActivePayloadIndex: (state, action: PayloadAction<number | null>) => {
      state.activePayloadIndex = action.payload;
    },

    closeMarkdownPanel: state => {
      state.activePayloadIndex = null;
    },

    // ========================================================================
    // Workflow Completion
    // ========================================================================

    completeWorkflow: state => {
      state.assistant.status = 'idle';
      state.assistant.currentStep = null;
      state.activities = {};
      state.activeToolCalls = [];
    },

    // ========================================================================
    // Error Handling
    // ========================================================================

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.assistant.status = 'idle';
      state.activeToolCalls = [];
    },

    clearError: state => {
      state.error = null;
    },

    // ========================================================================
    // SSE Performance Tracking
    // ========================================================================

    setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
      state.ssePerformanceMetrics = action.payload;
    },

    // ========================================================================
    // Reset
    // ========================================================================

    resetChat: () => initialState,

    newConversation: state => {
      state.sessionId = null;
      state.threadId = null;
      state.messages = [];
      state.activities = {};
      state.activeToolCalls = [];
      state.markdownPayloads = [];
      state.activePayloadIndex = null;
      state.assistant = initialState.assistant;
      state.error = null;
    },
  },
});

// Export actions
export const {
  setSessionId,
  setThreadId,
  setCurrentSkill,
  addUserMessage,
  setAssistantStatus,
  setCurrentStep,
  startStreamingMessage,
  appendStreamingContent,
  finalizeMessage,
  setActivitySnapshot,
  clearActivity,
  clearAllActivities,
  addToolCall,
  appendToolCallArgs,
  removeToolCall,
  clearToolCalls,
  addMarkdownPayload,
  setActivePayloadIndex,
  closeMarkdownPanel,
  completeWorkflow,
  setError,
  clearError,
  setSSEPerformanceMetrics,
  resetChat,
  newConversation,
} = agentChatSlice.actions;

// Export reducer
export default agentChatSlice.reducer;
